import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ShoppingCart, Sparkles } from "lucide-react";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Section "Tu pourrais aussi aimer" affichée sous une fiche produit.
 *
 * Logique de recommandation autonome :
 *  1. Priorité : même catégorie + en stock
 *  2. Fallback : autres produits en stock (les plus récents)
 *
 * Toujours 4 produits affichés, jamais le produit courant.
 */
export default function RelatedProducts({ currentProduct, useCart, limit = 4 }) {
  const [items, setItems] = useState([]);
  const navigate = useNavigate();
  const { addToCart } = useCart();

  useEffect(() => {
    if (!currentProduct?.product_id) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/products`);
        if (cancelled) return;
        const all = (res.data || [])
          .filter(p => p.product_id !== currentProduct.product_id)
          .filter(p => (p.stock || 0) > 0);

        // Score chaque produit selon affinité avec le produit courant
        const cat = currentProduct.category;
        const squareCats = new Set(currentProduct.square_categories || []);

        const scored = all.map(p => {
          let score = 0;
          if (cat && p.category === cat) score += 10;
          if (p.square_categories) {
            for (const sc of p.square_categories) {
              if (squareCats.has(sc)) score += 3;
            }
          }
          return { p, score };
        });

        // Tri : score décroissant, puis aléatoire (shuffle léger via date)
        scored.sort((a, b) => {
          if (b.score !== a.score) return b.score - a.score;
          return Math.random() - 0.5;
        });

        setItems(scored.slice(0, limit).map(x => x.p));
      } catch (e) {
        // silent fail — section just won't appear
      }
    })();
    return () => { cancelled = true; };
  }, [currentProduct, limit]);

  if (items.length === 0) return null;

  return (
    <section
      className="mt-16 pt-12 border-t border-white/10"
      data-testid="related-products"
    >
      <div className="mb-8">
        <h2 className="font-serif text-2xl md:text-3xl text-white mb-2">
          Tu pourrais aussi aimer
        </h2>
        <p className="text-sm text-[#B59ED1]">
          Une sélection autour de cet article, pour compléter ton rituel.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        {items.map(p => {
          const outOfStock = (p.stock || 0) <= 0;
          return (
            <article
              key={p.product_id}
              onClick={() => navigate(`/boutique/${p.product_id}`)}
              className="group cursor-pointer bg-[#261B3B] rounded-xl overflow-hidden border border-white/10 hover:border-[#D926A9]/50 transition-all"
              data-testid={`related-product-${p.product_id}`}
            >
              <div className="aspect-square overflow-hidden bg-[#1B132B] relative">
                {p.image_url ? (
                  <img
                    src={p.image_url}
                    alt={p.name}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Sparkles className="w-10 h-10 text-[#D926A9]/40" />
                  </div>
                )}
              </div>
              <div className="p-4">
                <Badge className="bg-[#3A115E] text-[#E2D5F8] text-[10px] mb-2">
                  {p.category}
                </Badge>
                <h3 className="font-serif text-sm md:text-base text-[#F7F3FC] group-hover:text-[#00E5FF] transition-colors line-clamp-2 mb-3 min-h-[2.5rem]">
                  {p.name}
                </h3>
                <div className="flex items-center justify-between">
                  <span className="text-base md:text-lg font-serif text-[#D926A9]">
                    {(p.price || 0).toFixed(2)} €
                  </span>
                  <Button
                    onClick={(e) => { e.stopPropagation(); addToCart(p); }}
                    size="sm"
                    className="gradient-primary h-8 w-8 p-0"
                    disabled={outOfStock}
                    data-testid={`related-add-${p.product_id}`}
                    title="Ajouter au panier"
                  >
                    <ShoppingCart className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}
